/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { View, Text, Icon } from '@components/atoms'
import { grey, blue, orange, green, blueGrey, red } from '@mui/material/colors'
import {
	Chip,
	Select,
	MenuItem,
	Button,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	SelectChangeEvent,
} from '@mui/material'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import {
	useAppDispatch,
	useAppSelector,
	inquiryMgmtActions,
	ApiGetDataStatus,
} from '@redux/index'
import {
	InquiryType,
	InquiryCommentType,
	InquiryStatusLogType,
	InquiryStatusType,
} from '@schemas'

dayjs.extend(utc)
dayjs.extend(timezone)

const STATUS_OPTIONS: { value: InquiryStatusType; label: string }[] = [
	{ value: 'new', label: '신규' },
	{ value: 'in_progress', label: '처리중' },
	{ value: 'pending', label: '대기' },
	{ value: 'resolved', label: '해결' },
	{ value: 'closed', label: '종료' },
]

const STATUS_COLOR: Record<string, string> = {
	new: blue[600],
	in_progress: orange[700],
	pending: grey[500],
	resolved: green[600],
	closed: blueGrey[700],
}

const STATUS_LABEL: Record<string, string> = {
	new: '신규',
	in_progress: '처리중',
	pending: '대기',
	resolved: '해결',
	closed: '종료',
}

type TimelineItemType =
	| { type: 'comment'; data: InquiryCommentType; createdAt: string }
	| { type: 'status'; data: InquiryStatusLogType; createdAt: string }

const InquiryDetailPage = () => {
	const reduxDispatch = useAppDispatch()
	const navigate = useNavigate()
	const { inquiryId } = useParams<{ inquiryId: string }>()

	const inquiry: InquiryType | null = useAppSelector(
		state => state.inquiryMgmt.selectedInquiry,
	)
	const detailStatus: ApiGetDataStatus = useAppSelector(
		state => state.inquiryMgmt.selectedInquiryStatus,
	)
	const comments: InquiryCommentType[] = useAppSelector(
		state => state.inquiryMgmt.comments,
	)
	const statusLogs: InquiryStatusLogType[] = useAppSelector(
		state => state.inquiryMgmt.statusLogs,
	)
	const userList: { id: string; name: string }[] = useAppSelector(
		state => state.inquiryMgmt.userList,
	)

	const [commentBody, setCommentBody] = useState('')
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [translatedMessage, setTranslatedMessage] = useState('')
	const [isTranslating, setIsTranslating] = useState(false)
	const [translateError, setTranslateError] = useState('')

	useEffect(() => {
		if (!inquiryId) return
		reduxDispatch(inquiryMgmtActions.SELECTED_INQUIRY({ id: inquiryId } as InquiryType))
		reduxDispatch(inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL(true))
	}, [inquiryId, reduxDispatch])

	useEffect(() => {
		setTranslatedMessage('')
		setTranslateError('')
	}, [inquiry?.id])

	const onTranslate = useCallback(async () => {
		if (!inquiry?.message || isTranslating) return
		setIsTranslating(true)
		setTranslateError('')
		try {
			const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=ko&dt=t&q=${encodeURIComponent(
				inquiry.message,
			)}`
			const res = await fetch(url)
			if (!res.ok) throw new Error('translation failed')
			const data = await res.json()
			const translated = (data?.[0] ?? [])
				.map((segment: any) => segment?.[0] ?? '')
				.join('')
			setTranslatedMessage(translated)
		} catch (err) {
			setTranslateError('번역에 실패했습니다.')
		} finally {
			setIsTranslating(false)
		}
	}, [inquiry, isTranslating])

	const userNameMap = useMemo(() => {
		const map: Record<string, string> = {}
		userList.forEach(u => {
			map[u.id] = u.name
		})
		return map
	}, [userList])

	const timeline: TimelineItemType[] = useMemo(() => {
		const items: TimelineItemType[] = [
			...comments.map(c => ({
				type: 'comment' as const,
				data: c,
				createdAt: c.createdAt,
			})),
			...statusLogs.map(s => ({
				type: 'status' as const,
				data: s,
				createdAt: s.createdAt,
			})),
		]
		items.sort(
			(a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
		)
		return items
	}, [comments, statusLogs])

	const onStatusChange = useCallback(
		(e: SelectChangeEvent<string>) => {
			if (!inquiry) return
			const nextStatus = e.target.value
			if (!nextStatus || nextStatus === inquiry.status) return
			reduxDispatch(
				inquiryMgmtActions.UPDATE_STATUS_PAYLOAD({
					inquiryId: inquiry.id,
					status: nextStatus,
					note: '',
				}),
			)
			reduxDispatch(inquiryMgmtActions.TRIGGER_UPDATE_STATUS(true))
		},
		[inquiry, reduxDispatch],
	)

	const onHandlerChange = useCallback(
		(e: SelectChangeEvent<string>) => {
			if (!inquiry) return
			reduxDispatch(
				inquiryMgmtActions.UPDATE_HANDLER_PAYLOAD({
					inquiryId: inquiry.id,
					handlerId: e.target.value,
				}),
			)
			reduxDispatch(inquiryMgmtActions.TRIGGER_UPDATE_HANDLER(true))
		},
		[inquiry, reduxDispatch],
	)

	const onAddComment = useCallback(() => {
		if (!inquiry || !commentBody.trim()) return
		reduxDispatch(
			inquiryMgmtActions.ADD_COMMENT_PAYLOAD({
				inquiryId: inquiry.id,
				body: commentBody,
				isInternal: true,
			}),
		)
		reduxDispatch(inquiryMgmtActions.TRIGGER_ADD_COMMENT(true))
		setCommentBody('')
	}, [inquiry, commentBody, reduxDispatch])

	const onDeleteComment = useCallback(
		(commentId: string) => {
			if (!inquiry) return
			reduxDispatch(
				inquiryMgmtActions.DELETE_COMMENT_PAYLOAD({
					inquiryId: inquiry.id,
					commentId,
				}),
			)
			reduxDispatch(inquiryMgmtActions.TRIGGER_DELETE_COMMENT(true))
		},
		[inquiry, reduxDispatch],
	)

	const onDeleteInquiry = useCallback(() => {
		if (!inquiry) return
		reduxDispatch(inquiryMgmtActions.DELETE_INQUIRY_ID(inquiry.id))
		reduxDispatch(inquiryMgmtActions.TRIGGER_DELETE_INQUIRY(true))
		setDeleteModalOpen(false)
	}, [inquiry, reduxDispatch])

	if (detailStatus === 'loading' || !inquiry || inquiry.id !== inquiryId) {
		return (
			<View css={style}>
				<View className="loadingView">
					<CircularProgress size={32} />
				</View>
			</View>
		)
	}

	if (!inquiry.name && !inquiry.email) {
		return (
			<View css={style}>
				<View className="loadingView">
					<Text category="p1">문의를 찾을 수 없습니다.</Text>
				</View>
			</View>
		)
	}

	return (
		<View css={style}>
			<View className="headerBar">
				<Button
					size="small"
					startIcon={<Icon name="arrow-left" />}
					onClick={() => navigate('/main/inquiry')}
					sx={{ color: grey[700], textTransform: 'none' }}
				>
					목록으로
				</Button>
				<Text category="h5" className="headerTitle">
					{`${inquiry.name || '-'} 님의 문의`}
				</Text>
				<View className="headerSpacer" />
				<Button
					size="small"
					variant="outlined"
					color="error"
					startIcon={<Icon name="trash-can-outline" />}
					onClick={() => setDeleteModalOpen(true)}
					sx={{ textTransform: 'none' }}
				>
					삭제
				</Button>
			</View>

			<View className="mainContent">
				<View className="leftCol">
					<View className="card">
						<View className="sectionTitle">
							<Text category="s1">문의 정보</Text>
						</View>
						<View className="infoGrid">
							<View className="infoRow">
								<Text category="label" className="infoLabel">
									고객명
								</Text>
								<Text category="p1">{inquiry.name || '-'}</Text>
							</View>
							<View className="infoRow">
								<Text category="label" className="infoLabel">
									이메일
								</Text>
								<Text category="p1">{inquiry.email || '-'}</Text>
							</View>
							{inquiry.phone && (
								<View className="infoRow">
									<Text category="label" className="infoLabel">
										전화번호
									</Text>
									<Text category="p1">{inquiry.phone}</Text>
								</View>
							)}
							{inquiry.nationality && (
								<View className="infoRow">
									<Text category="label" className="infoLabel">
										국적
									</Text>
									<Text category="p1">{inquiry.nationality}</Text>
								</View>
							)}
							<View className="infoRow">
								<Text category="label" className="infoLabel">
									카테고리
								</Text>
								<Text category="p1">{inquiry.categ || '-'}</Text>
							</View>
							<View className="infoRow">
								<Text category="label" className="infoLabel">
									접수일
								</Text>
								<Text category="p1">
									{inquiry.createdAt
										? dayjs(inquiry.createdAt).format('YYYY-MM-DD HH:mm:ss')
										: '-'}
								</Text>
							</View>
							<View className="infoRow">
								<Text category="label" className="infoLabel">
									상태
								</Text>
								<Select
									value={inquiry.status || ''}
									onChange={onStatusChange}
									size="small"
									displayEmpty
									sx={{ minWidth: 130, height: 32 }}
									renderValue={value =>
										value ? (
											<Chip
												label={STATUS_LABEL[value] ?? value}
												size="small"
												sx={{
													backgroundColor: STATUS_COLOR[value] ?? grey[400],
													color: '#FFF',
													fontWeight: 600,
													fontSize: 12,
												}}
											/>
										) : (
											<Text category="p2" hint="500">
												미설정
											</Text>
										)
									}
								>
									{STATUS_OPTIONS.map(opt => (
										<MenuItem key={opt.value} value={opt.value}>
											{opt.label}
										</MenuItem>
									))}
								</Select>
							</View>
							<View className="infoRow">
								<Text category="label" className="infoLabel">
									담당자
								</Text>
								<Select
									value={inquiry.handlerId || ''}
									onChange={onHandlerChange}
									size="small"
									displayEmpty
									sx={{ minWidth: 150, height: 32 }}
								>
									<MenuItem value="">
										<em>미배정</em>
									</MenuItem>
									{userList.map(u => (
										<MenuItem key={u.id} value={u.id}>
											{u.name}
										</MenuItem>
									))}
								</Select>
							</View>
						</View>
						<View className="messageDivider" />

						<View className="messageSection">
							<Text category="label" className="infoLabel">
								문의 내용
							</Text>
							<View className="messageBox">
								<Text category="p1" style={{ whiteSpace: 'pre-wrap' }}>
									{inquiry.message || '-'}
								</Text>
							</View>
							{!translatedMessage && (
								<View className="translateActions">
									<Button
										size="small"
										variant="outlined"
										onClick={() => {
											void onTranslate()
										}}
										disabled={!inquiry.message || isTranslating}
										startIcon={<Icon name="translate" />}
										sx={{ textTransform: 'none' }}
									>
										{isTranslating ? '번역 중...' : '한글로 번역'}
									</Button>
								</View>
							)}
							{translatedMessage && (
								<View className="messageBox translatedBox">
									<Text category="c1" hint="500" className="translatedLabel">
										한글 번역
									</Text>
									<Text category="p1" style={{ whiteSpace: 'pre-wrap' }}>
										{translatedMessage}
									</Text>
								</View>
							)}
							{translateError && (
								<Text category="c1" style={{ color: red[600], marginTop: '0.5rem' }}>
									{translateError}
								</Text>
							)}
						</View>
					</View>
				</View>

				<View className="rightCol">
					<View className="card">
						<View className="sectionTitle">
							<Text category="s1">활동 타임라인</Text>
						</View>
						<View className="timelineView">
							{timeline.length === 0 ? (
								<View className="emptyTimeline">
									<Text category="p2" hint="400">
										아직 활동 기록이 없습니다.
									</Text>
								</View>
							) : (
								timeline.map(item => {
									if (item.type === 'comment') {
										const comment = item.data
										const authorName =
											comment.authorName ||
											userNameMap[comment.authorId] ||
											comment.authorId
										return (
											<View key={`c-${comment.id}`} className="timelineItem commentItem">
												<View className="timelineHeader">
													<Icon
														name="comment-text-outline"
														style={{ fontSize: 16, color: blue[600] }}
													/>
													<Text category="c1" hint="500" className="timelineDate">
														{dayjs(comment.createdAt).format('YYYY-MM-DD HH:mm')}
													</Text>
													<Text category="s2" className="timelineAuthor">
														{authorName}
													</Text>
													<IconButton
														size="small"
														onClick={() => onDeleteComment(comment.id)}
														sx={{ marginLeft: 'auto', padding: '2px' }}
													>
														<Icon
															name="close"
															style={{ fontSize: 14, color: grey[400] }}
														/>
													</IconButton>
												</View>
												<Text category="p2" className="timelineBody">
													{comment.body}
												</Text>
											</View>
										)
									}
									const log = item.data
									const changerName =
										log.changedByName || userNameMap[log.changedBy] || log.changedBy
									return (
										<View key={`s-${log.id}`} className="timelineItem statusItem">
											<View className="timelineHeader">
												<View
													className="statusDot"
													style={{
														backgroundColor: STATUS_COLOR[log.toStatus] ?? grey[400],
													}}
												/>
												<Text category="c1" hint="500" className="timelineDate">
													{dayjs(log.createdAt).format('YYYY-MM-DD HH:mm')}
												</Text>
												<Text category="s2" className="timelineAuthor">
													{changerName}
												</Text>
											</View>
											<Text category="p2" className="timelineBody">
												{`상태 변경: ${
													STATUS_LABEL[log.fromStatus] ?? log.fromStatus ?? '-'
												} → ${STATUS_LABEL[log.toStatus] ?? log.toStatus}${
													log.note ? ` (${log.note})` : ''
												}`}
											</Text>
										</View>
									)
								})
							)}
						</View>

						<View className="commentInputView">
							<TextField
								value={commentBody}
								onChange={e => setCommentBody(e.target.value)}
								placeholder="코멘트를 입력하세요..."
								multiline
								rows={3}
								fullWidth
								size="small"
							/>
							<View className="commentActions">
								<Button
									variant="contained"
									onClick={onAddComment}
									disabled={!commentBody.trim()}
									sx={{ textTransform: 'none' }}
								>
									등록
								</Button>
							</View>
						</View>
					</View>
				</View>
			</View>

			<Dialog
				open={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>문의 삭제</DialogTitle>
				<DialogContent>
					<Text category="p2">정말 이 문의를 삭제하시겠습니까?</Text>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteModalOpen(false)}>취소</Button>
					<Button
						variant="contained"
						onClick={onDeleteInquiry}
						sx={{ backgroundColor: red[600] }}
					>
						삭제
					</Button>
				</DialogActions>
			</Dialog>
		</View>
	)
}

export default InquiryDetailPage

const style = css({
	width: '100%',
	height: '100%',
	overflow: 'auto',
	padding: '1rem',

	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '20rem',
	},

	'.headerBar': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.5rem',
		marginBottom: '1rem',

		'.headerTitle': {
			margin: 0,
		},
		'.headerSpacer': {
			flex: 1,
		},
	},

	'.mainContent': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
		gap: '1.5rem',
		alignItems: 'stretch',
		minHeight: 'calc(100vh - 8rem)',

		'@media (max-width: 1024px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.leftCol, .rightCol': {
		display: 'flex',
		flexDirection: 'column',
		gap: '1.5rem',
		minWidth: 0,
	},

	'.card': {
		background: '#FFF',
		borderRadius: '8px',
		padding: '1.25rem',
		border: `1px solid ${grey[200]}`,
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
	},

	'.sectionTitle': {
		marginBottom: '0.75rem',
		paddingBottom: '0.5rem',
		borderBottom: `1px solid ${grey[300]}`,
		textAlign: 'left',
	},

	'.sectionTitleRow': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: '0.5rem',
	},

	'.infoGrid': {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: '0.75rem 1.5rem',

		'@media (max-width: 1280px)': {
			gridTemplateColumns: '1fr',
		},
	},

	'.infoRow': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.75rem',
	},

	'.infoLabel': {
		minWidth: '4.5rem',
		color: grey[600],
	},

	'.messageDivider': {
		marginTop: '1rem',
		marginBottom: '0.5rem',
		borderTop: `1px solid ${grey[300]}`,
	},

	'.messageSection': {
		textAlign: 'left',
	},

	'.translateActions': {
		display: 'flex',
		justifyContent: 'flex-end',
		marginTop: '0.5rem',
	},

	'.messageBox': {
		marginTop: '0.5rem',
		padding: '1rem',
		background: grey[50],
		borderRadius: '6px',
		border: `1px solid ${grey[200]}`,
		textAlign: 'left',
	},

	'.translatedBox': {
		background: blue[50],
		borderColor: blue[200],
	},

	'.translatedLabel': {
		display: 'block',
		marginBottom: '0.25rem',
		color: blue[700],
	},

	'.timelineView': {
		display: 'flex',
		flexDirection: 'column',
		gap: '0.5rem',
		flex: 1,
		overflow: 'auto',
		minHeight: '10rem',
	},

	'.emptyTimeline': {
		display: 'flex',
		justifyContent: 'center',
		padding: '2rem',
	},

	'.timelineItem': {
		padding: '0.75rem',
		borderRadius: '6px',
		border: `1px solid ${grey[200]}`,
	},

	'.commentItem': {
		borderLeft: `3px solid ${blue[400]}`,
	},

	'.statusItem': {
		borderLeft: `3px solid ${grey[400]}`,
		background: grey[50],
	},

	'.timelineHeader': {
		display: 'flex',
		alignItems: 'center',
		gap: '0.5rem',
		marginBottom: '0.25rem',
	},

	'.timelineDate': {
		minWidth: 'fit-content',
	},

	'.timelineAuthor': {
		fontWeight: 600,
	},

	'.timelineBody': {
		paddingLeft: '1.5rem',
		marginTop: '0.25rem',
		whiteSpace: 'pre-wrap',
		textAlign: 'left',
	},

	'.statusDot': {
		width: 8,
		height: 8,
		borderRadius: '50%',
		flexShrink: 0,
	},

	'.commentInputView': {
		display: 'flex',
		flexDirection: 'column',
		marginTop: '1rem',
		paddingTop: '1rem',
		borderTop: `1px solid ${grey[200]}`,
	},

	'.commentActions': {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		marginTop: '0.5rem',
	},
})
